'use client';

import {
  useRef,
  useCallback,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { Box } from '@terra/ui-styled-system';
import {
  CodeMirrorMergeView,
  type CodeMirrorMergeViewHandle,
} from './CodeMirrorMergeView';
import {
  CodeMirrorUnifiedView,
  type CodeMirrorUnifiedViewHandle,
} from './CodeMirrorUnifiedView';
import { useComparerStore } from '../../store/comparer-store';

export type DiffViewerHandle = {
  goToNextDiff: () => void;
  goToPrevDiff: () => void;
  openSearch: () => void;
};

type DiffViewerProps = {
  leftContent: string;
  rightContent: string;
  leftLabel: string;
  rightLabel: string;
};

export const DiffViewer = forwardRef<DiffViewerHandle, DiffViewerProps>(
  function DiffViewer({ leftContent, rightContent }, ref) {
    const { settings, setTotalDiffs } = useComparerStore();

    const mergeRef = useRef<CodeMirrorMergeViewHandle>(null);
    const unifiedRef = useRef<CodeMirrorUnifiedViewHandle>(null);

    const getActiveRef = useCallback(() => {
      return settings.viewMode === 'unified'
        ? unifiedRef.current
        : mergeRef.current;
    }, [settings.viewMode]);

    // Update chunk count after content changes (small delay for CM to initialize)
    useEffect(() => {
      const timer = setTimeout(() => {
        const activeView = getActiveRef();
        if (activeView) {
          setTotalDiffs(activeView.getChunkCount());
        }
      }, 100);
      return () => clearTimeout(timer);
    }, [
      leftContent,
      rightContent,
      settings.viewMode,
      getActiveRef,
      setTotalDiffs,
    ]);

    const goToNextDiff = useCallback(() => {
      getActiveRef()?.goToNext();
    }, [getActiveRef]);

    const goToPrevDiff = useCallback(() => {
      getActiveRef()?.goToPrev();
    }, [getActiveRef]);

    const openSearch = useCallback(() => {
      getActiveRef()?.openSearch();
    }, [getActiveRef]);

    useImperativeHandle(
      ref,
      () => ({
        goToNextDiff,
        goToPrevDiff,
        openSearch,
      }),
      [goToNextDiff, goToPrevDiff, openSearch],
    );

    if (settings.viewMode === 'unified') {
      return (
        <Box h="full" w="full">
          <CodeMirrorUnifiedView
            ref={unifiedRef}
            leftContent={leftContent}
            rightContent={rightContent}
            collapseUnchanged={settings.collapseUnchanged}
          />
        </Box>
      );
    }

    return (
      <Box h="full" w="full">
        <CodeMirrorMergeView
          ref={mergeRef}
          leftContent={leftContent}
          rightContent={rightContent}
          collapseUnchanged={settings.collapseUnchanged}
        />
      </Box>
    );
  },
);
